<script lang="ts" setup>
import ClassLink from '../components/ClassLink.vue'
import SectionTitle from '@/components/Title/SectionTitle.vue'
import NewsItem from '../components/NewsItem.vue'
import Link2 from '@/components/Button/Link2.vue'
import Link3 from '@/components/Button/Link3.vue'
import Block from '@/components/Block/index.vue'
import { useArticleStore } from '@/stores'
import useSeo from './seo'

const route = useRoute()
const articleStore = useArticleStore()
await articleStore.fetchArticleCategories('newsCategories', {
  className: 'articleNews'
})
const showMore = ref(4)
const newsId = route.params.newsId as string
await articleStore.fetchArticleNews(newsId)
await articleStore.fetchArticleRecommends(newsId)
const news = computed(() => articleStore.getNews)
const recommends = computed(() => articleStore.recommends.filter((o, i) => i < showMore.value))

useSeo(news.value)
</script>

<template>
  <section class="news">
    <div class="news__leftbox">
      <div class="news__leftbox__inner">
        <div class="news__date__box">
          <div class="news__date__subtitle">{{ news?.brand_name }}</div>
          <Date class="news__date" :date="news?.publish_date" />
        </div>
        <ClassLink class="news__classlink" :category-id="news?.categories[0]?.id" />
        <Share class="news__share" :border="false" :round="true" />
      </div>
    </div>
    <div class="news__rightbox">
      <div class="news__rightbox__title">
        <h1>{{ news?.subject }}</h1>
      </div>
      <figure class="news__rightbox__banner">
        <Nimg :src="news?.image?.link" :title="news?.image?.title" :alt="news?.image?.alt" />
      </figure>
      <div class="tag-lists">
        <Tag class="tag-lists__tag" :name="tag?.name" v-for="(tag, index) in news?.tags" :key="tag.id" />
      </div>
      <div class="news__rightbox__content">
        <Block :blocks="news?.blocks" />
      </div>
      <div class="news__rightbox__bottom" v-if="recommends.length > 0">
        <SectionTitle class="mb-35">推薦閱讀</SectionTitle>
        <ul class="lists">
          <li v-for="(recommend, index) in recommends" :key="recommend.id">
            <NewsItem :item="recommend" />
          </li>
        </ul>
        <Link2 linkTitle="顯示更多" size="small" icon="icon-arrow-down3" class="cursor-pointer news__link2 circle"
          @click="showMore = 8" v-show="showMore === 4" />
        <Link3 linkTitle="顯示更多" class="cursor-pointer news__link3 circle" @click="showMore = 8"
          v-show="showMore === 4" />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.news {
  margin-top: toRem(50);
  @include grid;

  @include max-media(960) {
    grid-template-columns: repeat(4, 1fr);
  }

  &__leftbox {
    position: relative;
    grid-column: 1 / span 2;

    @include min-media(961) {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    @include max-media(960) {
      grid-column: 1 / span 4;
    }

    &__inner {
      display: flex;
      flex-direction: column;
      padding: toRem(150) 0;
      gap: toRem(15);

      @include min-media(961) {
        position: fixed;
        align-items: flex-end;
      }

      @include max-media(960) {
        align-items: center;
        padding: toRem(15) 0;
      }
    }
  }

  &__date {
    // margin-bottom: toRem(10);

    &__box {
      @include max-media(960) {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: toRem(15);
      }
    }

    &__subtitle {
      font-size: toRem(28);
      color: $color-gray-2;
      font-weight: 500;

      @include min-media(961) {
        display: none;
      }
    }
  }

  // &__classlink {
  //   margin-bottom: toRem(20);
  // }

  &__share {
    @include max-media(960) {
      display: none;
    }
  }

  &__rightbox {
    grid-column: 4 / span 8;

    @include max-media(960) {
      display: flex;
      flex-direction: column;
      grid-column: 1 / span 4;
    }

    &__title {
      font-size: toRem(28);
      margin-bottom: toRem(45);
      @include grid(8);

      @include max-media(480) {
        font-size: toRem(24);
      }

      h1 {
        grid-column: 2 / span 6;
        font-weight: 500;

        @include max-media(960) {
          grid-column: 1 / span 12;
        }
      }
    }

    &__banner {
      margin-bottom: toRem(55);

      @include max-media(960) {
        order: -1;
        margin-bottom: toRem(30);
      }

      img {
        display: block;
        width: 100%;
      }
    }

    &__content {
      margin-bottom: toRem(130);

      @include max-media(960) {
        margin-bottom: toRem(50);
      }
    }

    &__bottom {
      .lists {
        width: 100%;

        li {
          margin-bottom: toRem(55);
        }
      }
    }
  }

  &__link2 {
    @include max-media(480) {
      display: none;
    }
  }

  &__link3 {
    @include min-media(481) {
      display: none !important;
    }
  }
}

.tag-lists {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: toRem(60);

  &__tag {
    margin-left: toRem(5);
    margin-right: toRem(5);
  }
}
</style>
<style lang="scss">
.news__date__box {
  .date {
    &__day {
      @include max-media(1240) {
        font-size: toRem(60);
      }

      @include max-media(960) {
        font-size: toRem(36);
      }
    }

    &__month,
    &__year {
      @include max-media(1240) {
        font-size: toRem(20);
      }

      @include max-media(960) {
        font-size: toRem(12);
      }
    }
  }
}
</style>
